import React, { useEffect, useState } from 'react';

import type { CoreEnv, CoreUser } from '@eventbrite/legacy-infra.context-types';
import { GlobalNav } from '@eventbrite/consumer-header.global-nav';
import { ConsumerHeader } from '@eventbrite/consumer-header.consumer-header';
import styles from './GlobalHeader.module.scss';
import type {
    RequestShape,
    IntentToggleProps,
    FeatureFlags,
    searchBarProps,
} from '@eventbrite/consumer-header.global-nav/types';

export interface GlobalHeaderProps {
    env: Omit<Partial<CoreEnv>, 'localeInfo'> & {
        launchNewSignInSignUpLinks?: boolean;
        isManageOverviewActive?: boolean;
        localeInfo?: Partial<CoreEnv['localeInfo']>;
    };
    request: RequestShape;
    isHomePage?: boolean;
    intentToggle?: IntentToggleProps;
    featureFlags?: FeatureFlags;
    searchBar?: searchBarProps;
    /**
     * If true, the user will not be fetched from the API.
     * This prevents adding pressure to the homepage BFF.
     */
    disableFetchUser?: boolean;
}

const USER_API_ENDPOINTS = {
    consumerHeaderBff: '/consumer-header/api/user',
} as const;

const getUser = async (): Promise<{ user: CoreUser | null }> => {
    try {
        const response = await fetch(USER_API_ENDPOINTS.consumerHeaderBff);
        if (!response.ok) {
            throw new Error(`Failed to fetch user: ${response.statusText}`);
        }
        const user = await response.json();
        return user;
    } catch (e) {
        console.error('Error fetching user:', e);
        return { user: null };
    }
};

export const GlobalHeader: React.FunctionComponent<GlobalHeaderProps> = ({
    env,
    request,
    isHomePage,
    intentToggle,
    featureFlags,
    searchBar,
    disableFetchUser,
}) => {
    let header;
    //FIXME: fix types
    const coreEnv = env as CoreEnv;
    const [user, setUser] = useState<CoreUser | null>(null);

    useEffect(() => {
        if (!disableFetchUser) {
            const fetchUser = async () => {
                const { user } = await getUser();
                setUser(user);
            };
            fetchUser();
        }
    }, [disableFetchUser]);

    if (user) {
        header = (
            <ConsumerHeader
                env={coreEnv}
                request={request}
                user={user}
                isHomePage={isHomePage}
                featureFlags={featureFlags ?? {}}
                searchBar={searchBar}
            />
        );
    } else {
        header = (
            <GlobalNav user={{}} env={coreEnv} intentToggle={intentToggle} searchBar={searchBar} />
        );
    }

    return <div className={styles.root}>{header}</div>;
};
